import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { WebSocketProvider } from '@/contexts/WebSocketContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { InstructorRoute } from '@/components/auth/InstructorRoute';
import { AdminRoute } from '@/components/auth/AdminRoute';
import { LoginForm } from '@/components/auth/LoginForm';
import { Layout } from '@/components/Layout';
import { Toaster } from 'react-hot-toast';
import { store } from '@/store';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Lazy load pages
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const StakeholdersPage = lazy(() => import('@/pages/StakeholdersPage'));
const InterviewPage = lazy(() => import('@/pages/InterviewPage'));
const AssessmentPage = lazy(() => import('@/pages/AssessmentPage'));
const GapDetailsPage = lazy(() => import('@/pages/GapDetailsPage'));
const QuizPage = lazy(() => import('@/pages/QuizPage'));
const ReportPage = lazy(() => import('@/pages/ReportPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const SecurityArtifactsPage = lazy(() => import('@/pages/SecurityArtifactsPage'));
const InstructorDashboard = lazy(() => import('@/pages/InstructorDashboard'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const ThreatModelingPage = lazy(() => import('@/pages/ThreatModelingPage'));
const ThreatAdjudicationPage = lazy(() => import('@/pages/ThreatAdjudicationPage'));

// Admin pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const CoursesPage = lazy(() => import('@/pages/admin/CoursesPage'));
const CourseDetails = lazy(() => import('@/pages/admin/CourseDetails'));
const CohortDetails = lazy(() => import('@/pages/admin/CohortDetails'));

// Super Admin pages
const TenantsPage = lazy(() => import('@/pages/super-admin/TenantsPage'));
const AuditLogsPage = lazy(() => import('@/pages/super-admin/AuditLogsPage'));
const AnalyticsPage = lazy(() => import('@/pages/super-admin/AnalyticsPage'));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
  </div>
);

// Role-aware landing redirect for '/' — mirrors LoginForm.tsx's post-login role routing
// so instructors/admins bookmarking or refreshing on '/' land on their own dashboard
// instead of the student-shaped /dashboard.
const RootRedirect: React.FC = () => {
  const { userClaims } = useAuth();
  const role = userClaims?.role;
  if (role === 'instructor' || role === 'admin') {
    return <Navigate to="/instructor" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ErrorBoundary>
    <Provider store={store}>
      <ThemeProvider>
        <Router>
          <AuthProvider>
            <WebSocketProvider>
              <Toaster position="top-right" />
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <LoginForm />
              </div>
            } />
            
            <Route path="/signup" element={
              <Suspense fallback={<LoadingFallback />}>
                <SignupPage />
              </Suspense>
            } />
            
            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <RootRedirect />
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Dashboard />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/stakeholders" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <StakeholdersPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/interview/:stakeholderId" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <InterviewPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/interview" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <div className="text-center py-12">
                      <h1 className="text-3xl font-bold text-gray-900 mb-4">Interview</h1>
                      <p className="text-gray-600">Select a stakeholder from the Stakeholders page to start an interview.</p>
                    </div>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/assessment" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AssessmentPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/assessment/gap/:gapId" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <GapDetailsPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/assessment/quiz/:quizId" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <QuizPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* 'student' included: Report is the student capstone hub (discovered-gaps CSV export
                + instructions). The sidebar already showed it to students, but this guard bounced
                them to /unauthorized. Instructor-only content inside the page stays behind
                isInstructor. */}
            <Route path="/report" element={
              <ProtectedRoute allowedRoles={['admin', 'instructor', 'student']}>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ReportPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/artifacts" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <SecurityArtifactsPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />

            {/* Threat Modeling route guards mirror the sidebar. TM module content has shipped
                (spec 026 visual layer, Task 2) — students now have access. The adjudication
                routes below remain instructor-only (InstructorRoute) and are untouched. */}
            <Route path="/threat-modeling" element={
              <ProtectedRoute allowedRoles={['admin', 'instructor', 'student']}>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ThreatModelingPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/threat-modeling/:moduleId" element={
              <ProtectedRoute allowedRoles={['admin', 'instructor', 'student']}>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ThreatModelingPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/progress" element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <ProfilePage />
                </Suspense>
              </ProtectedRoute>
            } />
            
            <Route path="/instructor" element={
              <InstructorRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <InstructorDashboard />
                  </Suspense>
                </Layout>
              </InstructorRoute>
            } />

            <Route path="/users" element={
              <InstructorRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <UsersPage />
                  </Suspense>
                </Layout>
              </InstructorRoute>
            } />

            <Route path="/threat-modeling/adjudication" element={
              <InstructorRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ThreatAdjudicationPage />
                  </Suspense>
                </Layout>
              </InstructorRoute>
            } />

            <Route path="/threat-modeling/adjudication/:moduleId" element={
              <InstructorRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ThreatAdjudicationPage />
                  </Suspense>
                </Layout>
              </InstructorRoute>
            } />

            {/* Admin routes */}
            <Route path="/admin" element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard />
                  </Suspense>
                </Layout>
              </AdminRoute>
            } />

            <Route path="/admin/courses" element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <CoursesPage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            } />

            <Route path="/admin/courses/:courseId" element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <CourseDetails />
                  </Suspense>
                </Layout>
              </AdminRoute>
            } />

            <Route path="/admin/cohorts/:cohortId" element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <CohortDetails />
                  </Suspense>
                </Layout>
              </AdminRoute>
            } />

            {/* Redirect /admin/tenants to super admin tenants */}
            <Route path="/admin/tenants" element={
              <AdminRoute requireSuperAdmin>
                <Navigate to="/super-admin/tenants" replace />
              </AdminRoute>
            } />

            {/* Super Admin routes */}
            <Route path="/super-admin" element={
              <AdminRoute requireSuperAdmin>
                <Navigate to="/super-admin/tenants" replace />
              </AdminRoute>
            } />

            <Route path="/super-admin/tenants" element={
              <AdminRoute requireSuperAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <TenantsPage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            } />

            <Route path="/super-admin/audit-logs" element={
              <AdminRoute requireSuperAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AuditLogsPage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            } />

            <Route path="/super-admin/analytics" element={
              <AdminRoute requireSuperAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AnalyticsPage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            } />

            {/* Error pages */}
            <Route path="/unauthorized" element={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900">403</h1>
                  <p className="mt-2 text-gray-600">You don't have permission to access this page.</p>
                  <a href="/dashboard" className="mt-4 inline-block text-blue-600 hover:text-blue-500">
                    Return to Dashboard
                  </a>
                </div>
              </div>
            } />
            
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </WebSocketProvider>
        </AuthProvider>
      </Router>
      </ThemeProvider>
    </Provider>
    </ErrorBoundary>
  );
}

export default App;